import { CSSProperties, useEffect, useState } from "react";
import {
  UNSTABLE_ToastRegion as ToastRegion,
  UNSTABLE_Toast as Toast,
  UNSTABLE_ToastQueue as ToastQueue,
  UNSTABLE_ToastContent as ToastContent,
  ToastProps,
  Button,
  Text,
} from "react-aria-components";
import { composeTailwindRenderProps, cn } from "@/utils/helpers";
import { flushSync } from "react-dom";
import { Check, CircleAlert, Info, TriangleAlert, X } from "lucide-react";

type ToastVariant = "error" | "warning" | "info" | "success";
type ToastStyle = "default" | "dim";

interface MyToastContent {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** A short recovery action, such as undoing an optimistic calendar move. */
  action?: { label: string; onPress: () => void };
  /** Seconds the action stays open; shown after its label ("UNDO · 5") and counted down. */
  countdown?: number;
  /** The action label in capitals (the Signals desk's toasts: TRY AGAIN). */
  caps?: boolean;
  /** Kept for call-site compat; both styles share the platform panel look. */
  style?: ToastStyle;
}

/*
 * Toast (CreativeOS Library.lib.pen, Components / Status and feedback, style
 * B). A toast reports a consequence that already happened and names the object
 * ("Post moved to Sep 28", not "Saved"), with the one action that reverses or
 * follows it. Floating chrome (material tier 1), radius 16, no pegs. The glass
 * stays neutral; only the mark says something went wrong, and green is kept
 * for live.
 */
const variantIconClasses: Record<ToastVariant, string> = {
  success: "text-co-text2",
  error: "text-co-alert-ink",
  warning: "text-co-attention",
  info: "text-co-text2",
};

const variantIcons: Record<ToastVariant, React.FC<React.SVGProps<SVGSVGElement>>> = {
  success: Check,
  error: CircleAlert,
  info: Info,
  warning: TriangleAlert,
};

/* Settings M03: a toast stays 6 seconds, paused while the pointer or focus is
   on it (React Aria pauses on hover and focus). */
const DEFAULT_TOAST_TIMEOUT = 6000;

// This is a global toast queue, to be imported and called where ever you want to queue a toast via queue.add().
export const queue = new ToastQueue<MyToastContent>({
  maxVisibleToasts: 3,
  // Wrap state updates in a CSS view transition.
  wrapUpdate(fn) {
    if ("startViewTransition" in document) {
      document.startViewTransition(() => {
        flushSync(fn);
      });
    } else {
      fn();
    }
  },
});

const _originalAdd = queue.add.bind(queue);
queue.add = (content, options) => {
  return _originalAdd(content, {
    timeout: DEFAULT_TOAST_TIMEOUT,
    ...options,
  });
};

/** Follows CockpitLayout's data-co-theme so toasts flip with the shell. */
function useCoTheme(): "light" | "dark" {
  const [theme, setTheme] = useState<"light" | "dark">(
    () => (document.documentElement.dataset.coTheme as "light" | "dark") || "dark",
  );

  useEffect(() => {
    const el = document.documentElement;
    const sync = () =>
      setTheme((el.dataset.coTheme as "light" | "dark") || "dark");
    const obs = new MutationObserver(sync);
    obs.observe(el, { attributes: true, attributeFilter: ["data-co-theme"] });
    return () => obs.disconnect();
  }, []);

  return theme;
}

export function DefaultToastRegion() {
  const theme = useCoTheme();

  return (
    <ToastRegion
      queue={queue}
      className={cn(
        theme === "light" ? "co-tokens-light" : "co-tokens-dark",
        // pointer-events-none: region must not own a horizontal hit slab;
        // individual toasts re-enable with pointer-events-auto.
        "pointer-events-none fixed bottom-4 right-4 z-toast flex w-max max-w-[calc(100vw-2rem)] flex-col-reverse items-end gap-2",
        "outline-none focus-visible:outline-2 focus-visible:outline-co-accent focus-visible:outline-offset-2",
      )}
    >
      {({ toast }) => {
        const variant = toast.content.variant ?? "info";
        const Icon = variantIcons[variant];
        return (
          <MyToast
            toast={toast}
            className="pointer-events-auto"
          >
            <Icon aria-hidden className={cn("size-4 flex-none", variantIconClasses[variant])} />
            <ToastContent className="flex min-w-0 flex-1 flex-col gap-1">
              <Text
                slot="title"
                className="text-[13px] font-semibold leading-snug text-co-text"
              >
                {toast.content.title}
              </Text>
              {toast.content.description && (
                <Text
                  slot="description"
                  className="text-[12px] leading-[1.55] text-co-muted"
                >
                  {toast.content.description}
                </Text>
              )}
              {toast.content.action ? (
                <Button
                  onPress={() => {
                    toast.content.action?.onPress();
                    queue.close(toast.key);
                  }}
                  className="-ms-2 mt-0.5 inline-flex h-7 w-fit items-center rounded-pill px-2 text-[13px] font-semibold text-co-accent outline-none transition-colors hover:bg-co-accent-dim focus-visible:outline-2 focus-visible:outline-co-accent focus-visible:outline-offset-2"
                >
                  {toast.content.countdown ? (
                    <Countdown label={toast.content.action.label} seconds={toast.content.countdown} />
                  ) : (
                    <span className={toast.content.caps ? "uppercase" : undefined}>
                      {toast.content.action.label}
                    </span>
                  )}
                </Button>
              ) : null}
            </ToastContent>
            <Button
              slot="close"
              aria-label="Close"
              className="-me-1 grid size-7 flex-none place-items-center rounded-full border-none bg-transparent p-0 text-co-muted outline-none transition-colors hover:bg-co-hover hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent focus-visible:outline-offset-2"
            >
              <X aria-hidden className="size-3.5" />
            </Button>
          </MyToast>
        );
      }}
    </ToastRegion>
  );
}

/** An action label with the seconds left on it, in capitals (Signals / Toast · Undo). */
function Countdown({ label, seconds }: { label: string; seconds: number }) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    const started = Date.now();
    const id = window.setInterval(() => {
      setLeft(Math.max(1, seconds - Math.floor((Date.now() - started) / 1000)));
    }, 250);
    return () => window.clearInterval(id);
  }, [seconds]);
  return (
    <span className="uppercase">
      {label} · {left}
    </span>
  );
}

export function MyToast(props: ToastProps<MyToastContent>) {
  return (
    <Toast
      {...props}
      style={{ viewTransitionName: props.toast.key } as CSSProperties}
      className={composeTailwindRenderProps(
        props.className,
        cn(
          "co-glass-chrome flex w-[min(calc(100vw-2rem),320px)] items-center gap-3 rounded-panel px-3 py-3.5",
          "outline-none focus-visible:outline-2 focus-visible:outline-co-accent focus-visible:outline-offset-2",
          "[view-transition-class:toast]",
        ),
      )}
    />
  );
}
